import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Helmet } from "react-helmet-async";
import JoinNowFormPopup from "../../../componants/JoinNowFormPopup";

// Helper component for Star Rating (reused)
const StarRating = ({ rating }: { rating: number }) => {
  const stars = [];
  for (let i = 1; i <= 5; i++) {
    stars.push(
      <svg
        key={i}
        className={`h-4 w-4 ${
          i <= rating ? "text-yellow-400" : "text-gray-300"
        } inline-block`}
        fill="currentColor"
        viewBox="0 0 20 20"
      >
        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.538 1.118l-2.8-2.034a1 1 0 00-1.176 0l-2.8 2.034c-.783.57-1.838-.197-1.538-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.381-1.81.588-1.81h3.462a1 1 0 00.95-.69l1.07-3.292z" />
      </svg>
    );
  }
  return <div className="flex items-center">{stars}</div>;
};

// Helper component for Verified Badge (reused)
const VerifiedBadge = () => (
  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 ml-2">
    <svg
      className="w-3 h-3 mr-1"
      fill="currentColor"
      viewBox="0 0 20 20"
    >
      <path
        fillRule="evenodd"
        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
        clipRule="evenodd"
      />
    </svg>
    Verified
  </span>
);

const PPCExpert = () => {
  const testimonials = [
    {
      id: 1,
      name: "Akash Gupta",
      review:
        "The PPC expert course gave me a deep understanding of Google Ads and Bing Ads. I can now confidently set up, optimize, and scale campaigns. This is truly the best PPC training in Noida!",
      rating: 5,
      date: "2024-05-25",
      profileImg: "https://randomuser.me/api/portraits/men/38.jpg", // Placeholder image
      source: "Google Reviews",
    },
    {
      id: 2,
      name: "Divya Sharma",
      review:
        "Learning about advanced bidding strategies and audience targeting was incredibly insightful. The live campaign experience was invaluable. I landed a Digital Marketing Specialist role right after the course.",
      rating: 5,
      date: "2024-05-22",
      profileImg: "https://randomuser.me/api/portraits/women/42.jpg", // Placeholder image
      source: "LinkedIn",
    },
    {
      id: 3,
      name: "Rahul Kumar",
      review:
        "The instructors are top-notch, explaining complex concepts like conversion tracking and A/B testing with clarity. I now manage my client's PPC campaigns with much better results.",
      rating: 4,
      date: "2024-05-20",
      profileImg: "https://randomuser.me/api/portraits/men/58.jpg", // Placeholder image
      source: "Website Feedback",
    },
    {
      id: 4,
      name: "Pooja Das",
      review:
        "Fantastic course for anyone serious about paid advertising. I learned how to optimize ad copy, landing pages, and improve Quality Score. The practical focus made all the difference.",
      rating: 5,
      date: "2024-05-18",
      profileImg: "https://randomuser.me/api/portraits/women/68.jpg", // Placeholder image
      source: "Google Reviews",
    },
    {
      id: 5,
      name: "Vikas Singh",
      review:
        "Comprehensive and up-to-date content on Google Ads and social media advertising. The mentorship and placement support were excellent. I highly recommend this for aspiring PPC professionals.",
      rating: 4,
      date: "2024-05-15",
      profileImg: "https://randomuser.me/api/portraits/men/88.jpg", // Placeholder image
      source: "LinkedIn",
    },
  ];

  const [isPopupOpen, setIsPopupOpen] = useState(false); // State to control popup visibility
      const [selectedCourseTitle, setSelectedCourseTitle] = useState(""); // State to store the course title
    
      const handleOpenPopup = (courseTitle: string) => { // Function to open the popup
        setSelectedCourseTitle(courseTitle); // Set the selected course title
        setIsPopupOpen(true); // Open the popup
      };
    
      const handleClosePopup = () => { // Function to close the popup
        setIsPopupOpen(false); // Close the popup
        setSelectedCourseTitle(""); // Clear selected course when closing
      };

  const faqContent = [
    {
      title: "What is PPC and why is it essential for digital marketing?",
      answer:
        "PPC (Pay-Per-Click) is an online advertising model where advertisers pay a fee each time one of their ads is clicked. It's essential because it provides immediate visibility, precise targeting, measurable results, and quick traffic generation, making it a powerful tool for lead generation and sales.",
    },
    {
      title: "What are the prerequisites for joining this PPC Expert course?",
      answer:
        "No specific technical prerequisites are required. A basic understanding of digital marketing concepts and familiarity with internet usage is beneficial. This course is suitable for aspiring digital marketers, business owners, sales professionals, and anyone interested in paid advertising.",
    },
    {
      title: "What key PPC platforms and concepts will I master?",
      answer:
        "You will gain expertise in **Google Ads (Search, Display, Video, Shopping)**, **Bing Ads**, **Social Media Advertising (Facebook, Instagram)**, **Keyword Research for PPC**, Ad Copywriting, Landing Page Optimization, Bid Strategies, Audience Targeting, Conversion Tracking, A/B Testing, and campaign reporting.",
    },
    {
      title: "Is there a high demand for PPC professionals?",
      answer:
        "Yes, the demand for skilled PPC professionals is very high and continuously growing. Businesses of all sizes invest heavily in paid advertising to reach their target audience quickly and generate leads. PPC experts are crucial for maximizing ROI on advertising spend.",
    },
    {
      title: "Will I get placement assistance after completing the course?",
      answer:
        "Absolutely! We offer dedicated placement assistance to our PPC graduates. This includes resume development, mock interview preparation, soft skills training, and connecting you with leading digital marketing agencies and companies in Noida and across India for roles like PPC Specialist, Digital Marketing Executive, and Campaign Manager.",
    },
    {
      title: "What kind of practical projects will I work on?",
      answer:
        "You will gain hands-on experience by setting up and optimizing live PPC campaigns. This includes performing keyword research, creating compelling ad copy, designing landing pages, setting up conversion tracking, managing bids, analyzing campaign performance, and optimizing for ROI.",
    },
    {
      title: "What are the career opportunities after this PPC training?",
      answer:
        "Upon successful completion, you can pursue exciting careers as a PPC Specialist, Google Ads Expert, Social Media Advertiser, Digital Marketing Executive, Campaign Manager, Performance Marketing Specialist, or even a freelance PPC consultant.",
    },
    {
      title: "What is the course duration and mode of training?",
      answer:
        "The course duration varies based on the pace (intensive fast-track vs. regular batch). We provide flexible training options including immersive offline classroom training at our Noida center and interactive live online classes.",
    },
  ];

  const curriculumContent = [
    {
      title: "Module 1: Introduction to PPC & Digital Advertising",
      description: "Understanding the PPC ecosystem, how paid search works, key metrics (CPC, CTR, CPA, ROAS), and overall digital marketing strategy.",
    },
    {
      title: "Module 2: Google Ads Fundamentals (Search Campaigns)",
      description: "Setting up your Google Ads account, campaign structure, ad groups, keyword types (match types), and writing effective search ad copy.",
    },
    {
      title: "Module 3: Advanced Google Ads Strategies",
      description: "Automated bidding strategies, Quality Score optimization, ad extensions, negative keywords, remarketing lists for search ads (RLSA), and advanced targeting.",
    },
    {
      title: "Module 4: Google Display Network (GDN)",
      description: "Understanding GDN, various targeting methods (audiences, topics, placements), creating compelling display ads, and measuring performance.",
    },
    {
      title: "Module 5: YouTube Ads (Video Campaigns)",
      description: "Types of YouTube ads, audience targeting, creating engaging video ad creatives, and optimizing video campaigns for views and conversions.",
    },
    {
      title: "Module 6: Google Shopping Ads (eCommerce PPC)",
      description: "Setting up Google Merchant Center, creating product feeds, structuring Shopping campaigns, and optimizing product listings for retail.",
    },
    {
      title: "Module 7: Social Media Advertising (Facebook & Instagram Ads)",
      description: "Creating campaigns on Facebook Ads Manager, audience targeting (demographics, interests, behaviors), ad formats, and campaign objectives.",
    },
    {
      title: "Module 8: LinkedIn & Other Social Platforms",
      description: "Introduction to LinkedIn Ads, Twitter Ads, and other relevant social media advertising platforms, focusing on professional networking and niche targeting.",
    },
    {
      title: "Module 9: Landing Page Optimization & A/B Testing",
      description: "Designing high-converting landing pages, A/B testing ad copy and landing page elements, and improving user experience.",
    },
    {
      title: "Module 10: Conversion Tracking & Analytics",
      description: "Implementing conversion tracking (Google Ads, Facebook Pixel), integrating with Google Analytics, and interpreting performance data for optimization.",
    },
    {
      title: "Module 11: PPC Reporting & Client Management",
      description: "Creating comprehensive PPC reports, communicating insights to clients, budgeting, and managing multiple campaigns efficiently.",
    },
    {
      title: "Module 12: Live Project & Campaign Management",
      description: "Hands-on experience managing and optimizing a live PPC campaign, from setup to advanced reporting and troubleshooting, building a strong portfolio.",
    },
  ];

  const [currentTestimonialIndex, setCurrentTestimonialIndex] = useState(0);

  useEffect(() => {
    const autoScrollInterval = setInterval(() => {
      setCurrentTestimonialIndex(
        (prevIndex) => (prevIndex + 1) % testimonials.length
      );
    }, 5000); // Auto-slide every 5 seconds

    return () => clearInterval(autoScrollInterval); // Clean up the interval
  }, [testimonials.length]);

  const goToPrevTestimonial = () => {
    setCurrentTestimonialIndex((prevIndex) =>
      prevIndex === 0 ? testimonials.length - 1 : prevIndex - 1
    );
  };

  const goToNextTestimonial = () => {
    setCurrentTestimonialIndex((prevIndex) =>
      (prevIndex + 1) % testimonials.length
    );
  };

  const getVisibleTestimonialsCount = () => {
    if (window.innerWidth >= 1024) return 3; // lg breakpoint
    if (window.innerWidth >= 768) return 2; // md breakpoint
    return 1; // default for small screens
  };

  const [visibleCount, setVisibleCount] = useState(
    getVisibleTestimonialsCount()
  );

  useEffect(() => {
    const handleResize = () => {
      setVisibleCount(getVisibleTestimonialsCount());
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const startIndex = currentTestimonialIndex;
  const currentVisibleTestimonials = [];

  for (let i = 0; i < visibleCount; i++) {
    currentVisibleTestimonials.push(
      testimonials[(startIndex + i) % testimonials.length]
    );
  }

  return (
    <div className="bg-white text-gray-800">
      <Helmet>
        <title>PPC Expert Course in Noida | Google Ads, Facebook Ads Training</title>
        <meta
          name="description"
          content="Become a certified PPC Expert in Noida. Master Google Ads (Search, Display, Video, Shopping) and Social Media Ads with practical training and 100% placement assistance."
        />
        <meta
          name="keywords"
          content="PPC expert course Noida, PPC training, Google Ads course, Facebook Ads training, Paid Advertising, Digital Marketing, SEM, Search Engine Marketing, Campaign Management, Conversion Tracking, PPC certification"
        />
      </Helmet>

      {/* Hero Section */}
      <section className="relative overflow-hidden text-white h-[400px] bg-gray-900 flex items-center">
        <img
          src="https://media.designrush.com/articles/608361/conversions/12-Best-PPC-platforms-in-2024-(2)-preview.jpg"
          alt="PPC Expert Course Background"
          className="absolute inset-0 w-full h-full object-cover z-0"
        />

        {/* Dark overlay */}
        <div className="absolute inset-0 bg-black bg-opacity-60 z-10" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 py-32 md:py-40 flex flex-col md:flex-row items-center md:items-start">
          <div className="md:w-1/2">
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-4xl md:text-5xl font-extrabold leading-tight text-left"
            >
              Become a <span className="text-orange-500">PPC Expert</span> in Noida
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.8 }}
              className="mt-6 text-lg md:text-xl text-gray-200 text-left"
            >
              Master Pay-Per-Click advertising on Google Ads, Facebook, and more to drive instant traffic and conversions.
            </motion.p>

            <motion.button
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.8 }}
              className="mt-8 bg-orange-700 hover:bg-orange-600 text-white font-bold py-3 px-8 rounded-full shadow-lg transition duration-300 ease-in-out"
            >
              <button
                    // className="bg-orange-600 hover:bg-orange-700 px-6 py-3 rounded-lg font-semibold transition text-white"
                    onClick={() => handleOpenPopup("Full Stack Development Training")} // Pass the course title
                  >
                    Enroll Now !
                  </button>
            </motion.button>
          </div>
        </div>
      </section>

      {/* Why Learn PPC Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-8 text-blue-900">Why Become a PPC Expert?</h2>
          <p className="text-lg text-gray-700 mb-8 max-w-3xl mx-auto">
            PPC advertising offers immediate results and precise targeting, making it indispensable for rapid business growth. As a PPC expert, you'll manage budgets, optimize campaigns, and drive measurable ROI, a highly valued skill in today's digital economy.
          </p>
          <div className="grid md:grid-cols-3 gap-8 mt-10">
            <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300">
              <h3 className="font-semibold text-xl text-red-700 mb-3">Instant Visibility & Traffic</h3>
              <p className="text-gray-600">Reach target audiences immediately and drive qualified traffic.</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300">
              <h3 className="font-semibold text-xl text-red-700 mb-3">High Demand Profession</h3>
              <p className="text-gray-600">Businesses are constantly looking for skilled PPC managers.</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300">
              <h3 className="font-semibold text-xl text-red-700 mb-3">Measurable ROI & Growth</h3>
              <p className="text-gray-600">Directly impact sales and monitor campaign performance effectively.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Course Highlights Section */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center text-blue-900">Course Highlights</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { icon: '🎯', title: 'Comprehensive Platform Coverage', desc: 'Master Google Ads, Facebook Ads, Instagram Ads, and more.' },
              { icon: '🚀', title: 'Live Campaign Management', desc: 'Gain hands-on experience by managing real-time PPC campaigns.' },
              { icon: '👨‍🏫', title: 'Industry Expert Trainers', desc: 'Learn advanced strategies from certified PPC professionals.' },
              { icon: '🎓', title: 'Certifications & Portfolio', desc: 'Earn recognized certifications and build a strong campaign portfolio.' },
              { icon: '🤝', title: '100% Placement Assistance', desc: 'Dedicated support for job search, resume building, and interviews.' },
              { icon: '📊', title: 'Advanced Analytics & Reporting', desc: 'Proficiency in tracking conversions, interpreting data, and creating reports.' },
            ].map((item, index) => (
              <motion.div
                key={index}
                className="bg-red-50 p-6 rounded-lg shadow-md flex items-start space-x-4"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <div className="text-4xl">{item.icon}</div>
                <div>
                  <h3 className="font-semibold text-xl text-red-800 mb-2">{item.title}</h3>
                  <p className="text-gray-700">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Curriculum Overview Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center text-blue-900">Detailed Curriculum Overview</h2>
          <div className="space-y-4">
            {curriculumContent.map((module, idx) => (
              <div
                key={idx}
                className="border border-gray-200 rounded-lg shadow-sm overflow-hidden bg-white"
              >
                <details className="group">
                  <summary className="cursor-pointer px-6 py-4 font-semibold text-lg bg-gray-50 hover:bg-gray-100 transition duration-200 ease-in-out flex justify-between items-center">
                    {module.title}
                    <span className="transform transition-transform duration-200 group-open:rotate-180">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </span>
                  </summary>
                  <div className="px-6 py-4 text-gray-700 leading-relaxed border-t border-gray-200">
                    <p>{module.description}</p>
                  </div>
                </details>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tools & Platforms */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-8 text-blue-900">Tools & Platforms You'll Master</h2>
          <p className="text-lg text-gray-700 mb-10 max-w-3xl mx-auto">
            Our PPC expert training provides extensive hands-on experience with the leading advertising platforms and analytical tools.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-8 items-center justify-center">
            {[
              { name: 'Google Ads', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/google/google-original.svg', desc: 'Search, Display, Video, Shopping' },
              { name: 'Facebook Ads Manager', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/facebook/facebook-original.svg', desc: 'Meta Business Suite' },
              { name: 'Google Analytics 4', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/google/google-original.svg', desc: 'Campaign tracking & reporting' },
              { name: 'Google Tag Manager', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/google/google-original.svg', desc: 'Efficient tag implementation' },
              { name: 'Bing Ads (Microsoft Ads)', logo: 'https://seeklogo.com/images/B/bing-ads-logo-A0580B2B58-seeklogo.com.png', desc: 'Search engine advertising' },
              { name: 'CRM Integration Concepts', logo: 'https://5.imimg.com/data5/UL/SW/MY-10608141/crm-callcenter-integration-500x500.jpg', desc: 'Connecting ads to sales' },
            ].map((tool, index) => (
              <motion.div
                key={index}
                className="flex flex-col items-center p-4 bg-gray-100 rounded-lg shadow-sm text-center"
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
              >
                <img src={tool.logo} alt={tool.name} className="h-12 mb-2 object-contain" />
                <p className="text-gray-700 font-medium text-sm">{tool.name}</p>
                {tool.desc && <p className="text-gray-500 text-xs mt-1">{tool.desc}</p>}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Career Opportunities */}
      <section className="py-16 bg-red-50">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-8 text-blue-900">Exciting Career Opportunities in PPC</h2>
          <p className="text-lg text-gray-700 mb-10 max-w-3xl mx-auto">
            A certification as a **PPC Expert** will position you for high-growth roles in digital advertising, where your skills directly contribute to business revenue.
          </p>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: '📈', title: 'PPC Specialist', desc: 'Design, execute, and optimize paid search and social campaigns.' },
              { icon: '🌐', title: 'SEM Specialist', desc: 'Focus on both paid search (PPC) and organic search (SEO) strategies.' },
              { icon: '🎯', title: 'Digital Marketing Executive', desc: 'Manage various digital channels including paid advertising.' },
              { icon: '💰', title: 'Campaign Manager', desc: 'Oversee and optimize advertising budgets and campaign performance.' },
              { icon: '💻', title: 'Performance Marketing Specialist', desc: 'Drive measurable results through various paid channels.' },
              { icon: '💡', title: 'Freelance PPC Consultant', desc: 'Offer your expertise to multiple clients, managing their ad spend.' },
            ].map((role, index) => (
              <motion.div
                key={index}
                className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 flex flex-col items-center text-center"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <div className="text-5xl mb-4">{role.icon}</div>
                <h3 className="font-semibold text-xl text-red-800 mb-2">{role.title}</h3>
                <p className="text-gray-700">{role.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="max-w-6xl mx-auto px-4 py-16 grid md:grid-cols-3 gap-10">
        <div className="md:col-span-2">
          <h2 className="text-3xl md:text-4xl font-bold mb-8 text-center md:text-left">Frequently Asked Questions</h2>
          <div className="space-y-4">
            {faqContent.map((section, idx) => (
              <div
                key={idx}
                className="border border-gray-200 rounded-lg shadow-sm overflow-hidden bg-white"
              >
                <details className="group">
                  <summary className="cursor-pointer px-6 py-4 font-semibold text-lg bg-gray-50 hover:bg-gray-100 transition duration-200 ease-in-out flex justify-between items-center">
                    {section.title}
                    <span className="transform transition-transform duration-200 group-open:rotate-180">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </span>
                  </summary>
                  <div className="px-6 py-4 text-gray-700 leading-relaxed border-t border-gray-200">
                    <p>{section.answer}</p>
                  </div>
                </details>
              </div>
            ))}
          </div>
        </div>

        {/* Enquiry Form */}
        <div className="md:col-span-1 pt-20">
          <div className="bg-gray-100 rounded-lg p-6 shadow-xl">
            <h3 className="text-2xl font-bold mb-4 text-center text-blue-800">
              Enquire Now
            </h3>
            <p className="text-center text-gray-600 mb-6">
              Fill out the form below to get more details about our PPC Expert training program.
            </p>
            <form className="space-y-4">
              <input
                type="text"
                placeholder="Your Name"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
              <input
                type="text"
                placeholder="Course of Interest (e.g., PPC Expert Course)"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
              <input
                type="text"
                placeholder="College/University Name (Optional)"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <input
                type="email"
                placeholder="Your Email"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
              <input
                type="tel"
                placeholder="Phone Number"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
              <button
                type="submit"
                className="w-full bg-blue-800 hover:bg-blue-700 text-white py-3 rounded-md font-semibold transition duration-300 ease-in-out shadow-md hover:shadow-lg"
              >
                JOIN THE PROGRAM
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* Testimonial Section - No Horizontal Scrollbar */}
      <section className="bg-red-50 py-16">
        <div className="max-w-7xl mx-auto px-4 text-center relative">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-red-900">
            What Our PPC Expert Students Say
          </h2>

          <div className="relative overflow-hidden w-full mx-auto">
            {/* Left Scroll Button */}
            <button
              onClick={goToPrevTestimonial}
              className="absolute left-0 top-1/2 -translate-y-1/2 z-10 p-3 bg-white rounded-full shadow-lg hover:bg-gray-100 transition-colors duration-300 hidden md:block"
              aria-label="Previous testimonial"
            >
              <svg
                className="w-6 h-6 text-gray-700"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M15 19l-7-7 7-7"
                ></path>
              </svg>
            </button>

            {/* Testimonial Cards Container */}
            <div className="flex justify-center items-stretch space-x-6 md:space-x-8 lg:space-x-10">
              <AnimatePresence mode="wait" initial={false}>
                {currentVisibleTestimonials.map((testimonial, idx) => (
                  <motion.div
                    key={testimonial.id}
                    initial={{ opacity: 0, x: idx * 100 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -100 }}
                    transition={{ duration: 0.5, ease: "easeOut" }}
                    className="flex-none w-full md:w-[calc(50%-1.5rem)] lg:w-[calc(33.333%-2rem)] p-2"
                  >
                    <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100 flex flex-col h-full text-left">
                      <div className="flex items-center mb-4">
                        <img
                          src={testimonial.profileImg}
                          alt={testimonial.name}
                          className="w-12 h-12 rounded-full object-cover mr-4 border-2 border-red-200"
                        />
                        <div>
                          <p className="font-bold text-gray-900 text-lg">
                            {testimonial.name}
                          </p>
                          <p className="text-gray-500 text-sm">
                            {testimonial.date} • {testimonial.source}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center mb-3">
                        <StarRating rating={testimonial.rating} />
                        {testimonial && <VerifiedBadge />}
                      </div>
                      <p className="text-gray-700 text-base leading-relaxed flex-grow line-clamp-4">
                        {testimonial.review}
                      </p>
                      <a
                        href="#"
                        className="text-red-600 hover:underline mt-4 text-sm text-right"
                      >
                        Read more
                      </a>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>

            {/* Right Scroll Button */}
            <button
              onClick={goToNextTestimonial}
              className="absolute right-0 top-1/2 -translate-y-1/2 z-10 p-3 bg-white rounded-full shadow-lg hover:bg-gray-100 transition-colors duration-300 hidden md:block"
              aria-label="Next testimonial"
            >
              <svg
                className="w-6 h-6 text-gray-700"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M9 5l7 7-7 7"
                ></path>
              </svg>
            </button>
          </div>

          {/* Dot Indicators */}
          <div className="flex justify-center mt-8 space-x-2">
            {testimonials.map((_, idx) => (
              <button
                key={idx}
                className={`h-3 w-3 rounded-full ${
                  idx >= currentTestimonialIndex &&
                  idx < currentTestimonialIndex + visibleCount
                    ? "bg-red-600"
                    : "bg-gray-300"
                } transition-colors duration-300`}
                onClick={() => setCurrentTestimonialIndex(idx)}
                aria-label={`Go to testimonial group ${idx + 1}`}
              ></button>
            ))}
          </div>
        </div>
      </section>

      {/* Conditionally render the popup */}
      {isPopupOpen && (
        <JoinNowFormPopup
          onClose={handleClosePopup}
          courseTitle={selectedCourseTitle}
        />
      )}
    </div>
  );
};

export default PPCExpert;